import { type BackendProduct } from './api/schema/plansSchema';
import { localizePlanName } from '@/components/new-order/lang/planLocalization';

const customMap = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CAD: 'CAD $',
  NZD: 'NZD $',
  AUD: 'AUD $',
} as const;

const externalMap = {
  USD: '$',
  CAD: '$',
  EUR: '€',
  AED: 'د.إ.‏',
  AFN: '؋',
  ALL: 'Lek',
  AMD: 'դր.',
  ARS: '$',
  AUD: '$',
  AZN: 'ман.',
  BAM: 'KM',
  BDT: '৳',
  BGN: 'лв.',
  BHD: 'د.ب.‏',
  BIF: 'FBu',
  BND: '$',
  BOB: 'Bs',
  BRL: 'R$',
  BWP: 'P',
  BYN: 'руб.',
  BZD: '$',
  CDF: 'FrCD',
  CHF: 'CHF',
  CLP: '$',
  CNY: 'CN¥',
  COP: '$',
  CRC: '₡',
  CVE: 'CV$',
  CZK: 'Kč',
  DJF: 'Fdj',
  DKK: 'kr',
  DOP: 'RD$',
  DZD: 'د.ج.‏',
  EEK: 'kr',
  EGP: 'ج.م.‏',
  ERN: 'Nfk',
  ETB: 'Br',
  GBP: '£',
  GEL: 'GEL',
  GHS: 'GH₵',
  GNF: 'FG',
  GTQ: 'Q',
  HKD: '$',
  HNL: 'L',
  HRK: 'kn',
  HUF: 'Ft',
  IDR: 'Rp',
  ILS: '₪',
  INR: 'টকা',
  IQD: 'د.ع.‏',
  IRR: '﷼',
  ISK: 'kr',
  JMD: '$',
  JOD: 'د.أ.‏',
  JPY: '￥',
  KES: 'Ksh',
  KHR: '៛',
  KMF: 'FC',
  KRW: '₩',
  KWD: 'د.ك.‏',
  KZT: 'тңг.',
  LBP: 'ل.ل.‏',
  LKR: 'SL Re',
  LTL: 'Lt',
  LVL: 'Ls',
  LYD: 'د.ل.‏',
  MAD: 'د.م.‏',
  MDL: 'MDL',
  MGA: 'MGA',
  MKD: 'MKD',
  MMK: 'K',
  MOP: 'MOP$',
  MUR: 'MURs',
  MXN: '$',
  MYR: 'RM',
  MZN: 'MTn',
  NAD: 'N$',
  NGN: '₦',
  NIO: 'C$',
  NOK: 'kr',
  NPR: 'नेरू',
  NZD: '$',
  OMR: 'ر.ع.‏',
  PAB: 'B/.',
  PEN: 'S/.',
  PHP: '₱',
  PKR: '₨',
  PLN: 'zł',
  PYG: '₲',
  QAR: 'ر.ق.‏',
  RON: 'RON',
  RSD: 'дин.',
  RUB: '₽.',
  RWF: 'FR',
  SAR: 'ر.س.‏',
  SDG: 'SDG',
  SEK: 'kr',
  SGD: '$',
  SOS: 'Ssh',
  SYP: 'ل.س.‏',
  THB: '฿',
  TND: 'د.ت.‏',
  TOP: 'T$',
  TRY: 'TL',
  TTD: '$',
  TWD: 'NT$',
  TZS: 'TSh',
  UAH: '₴',
  UGX: 'USh',
  UYU: '$',
  UZS: 'UZS',
  VEF: 'Bs.F.',
  VND: '₫',
  XAF: 'FCFA',
  XOF: 'CFA',
  YER: 'ر.ي.‏',
  ZAR: 'R',
  ZMK: 'ZK',
  ZWL: 'ZWL$',
} as const;

const currencySymbolMap = { ...externalMap, ...customMap } as const;

const isKnownCurrency = (currencyCode: string): currencyCode is keyof typeof currencySymbolMap => {
  return (Object.keys(currencySymbolMap) as readonly string[]).includes(currencyCode);
};

const moneyStructToString = (moneyStruct: { amount: number; currency: string }): string => {
  const { amount, currency } = moneyStruct;

  const currencyLabel = isKnownCurrency(currency) ? currencySymbolMap[currency] : currency;
  const cost = String(amount).padStart(3, '0');

  return `${currencyLabel}${cost.slice(0, cost.length - 2) || '0'}.${cost.slice(cost.length - 2)}`;
};

const parseSavePercentage = (product: BackendProduct): string => {
  const value = product.data.discount?.en;

  if (value?.endsWith('%')) return value.replace('%', '');

  return value ?? '';
};

const calculateTotalExcludingTaxes = (p: BackendProduct, moneyStruct: { amount: number; currency: string }): string => {
  if (p.taxes.length === 0) {
    return moneyStructToString(moneyStruct);
  }

  // `total` - accumulated total of taxes
  const amount = moneyStruct.amount - p.taxes.reduce((acc, cur) => (acc += cur.amount), 0);

  return moneyStructToString({ amount, currency: moneyStruct.currency });
};

const calculateMonthlyExcludingTaxes = (
  p: BackendProduct,
  moneyStruct: { amount: number; currency: string },
  totalAmount: number,
): string => {
  if (p.taxes.length === 0) {
    return moneyStructToString(moneyStruct);
  }

  const durationMonths = Math.round(totalAmount / moneyStruct.amount);

  // (totalAmount - taxes) / planDurationMonths
  const amount = Math.ceil((totalAmount - p.taxes.reduce((acc, cur) => (acc += cur.amount), 0)) / durationMonths);

  return moneyStructToString({ amount, currency: moneyStruct.currency });
};

export const mapProductObject = (p: BackendProduct) => ({
  id: p.id,
  index: p.index,
  name: localizePlanName(p),
  sku: p.sku,
  type: p.product_type,

  monthlyCost: moneyStructToString(p.data.price_per_month),
  fullPriceBeforeDiscount: moneyStructToString(p.data.full_price_before_discount),
  fullPriceAfterDiscount: moneyStructToString(p.data.full_price_after_discount),
  discountInCurrency: moneyStructToString(p.data.discount_amount_in_currency),
  promocodeInCurrency: moneyStructToString(p.data.coupon_discount_amount),
  totalSavings: moneyStructToString(p.data.total_savings),
  orderTotal: moneyStructToString(p.data.total_order_amount),

  noTaxMonthlyCost: calculateMonthlyExcludingTaxes(p, p.data.price_per_month, p.data.total_order_amount.amount),
  noTaxTotal: calculateTotalExcludingTaxes(p, p.data.total_order_amount),
  noTaxInitialTotal: p.data.initial_total_order_amount
    ? calculateTotalExcludingTaxes(p, p.data.initial_total_order_amount)
    : null,
  noTaxInitialMonthlyCost:
    p.data.initial_price_per_month && p.data.initial_total_order_amount
      ? calculateMonthlyExcludingTaxes(p, p.data.initial_price_per_month, p.data.initial_total_order_amount.amount)
      : null,

  savePercentage: parseSavePercentage(p),
  showBestValueLabel: p.best_price,
  showSpecialOfferLabel: false,

  hasTrial: p.has_trial,
  trialDays: p.trial_period_in_days,

  formattedCurrency: isKnownCurrency(p.data.total_order_amount.currency)
    ? currencySymbolMap[p.data.total_order_amount.currency]
    : p.data.total_order_amount.currency,

  // this piece is Stripe PaymentRequest related
  // docs: https://stripe.com/docs/js/payment_request/create
  paymentRequest: {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- can occasionaly be a string
    amount: Number(p.data.total_order_amount.amount),
    currency: p.data.total_order_amount.currency.toLowerCase(),
    label: `ClearVPN ${p.name} Subscription ${p.has_trial ? ' with trial' : ''}`,
  },

  analyticsData: {
    name: p.name,
    id: p.sku,
    price: p.data.full_price_after_discount.amount / 100,
    quantity: '1',
    subscriptionDuration: p.duration / 24,
    trialDuration: p.trial_period_in_days,
  },
  currencyCode: p.data.full_price_after_discount.currency,
  revenue: p.data.total_order_amount.amount / 100,

  taxes: p.taxes.map((tax) => ({
    ...tax,
    amount: moneyStructToString({ amount: tax.amount, currency: p.data.total_order_amount.currency }),
  })),
});
export type Plan = ReturnType<typeof mapProductObject>;

export const mapProductsArray = (products: BackendProduct[]): Plan[] =>
  products.map((p) => mapProductObject(p)).sort((a, b) => a.index - b.index);
